import { Box, Tab, Tabs, useTheme } from '@mui/material'
import React from 'react'

interface SharedTabsProps {
  tabs: React.ReactNode[]
  value: number
  onChange: (newValue: number) => void
  ariaLabel?: string
  /** Which tabs have a rendered `shared-tabpanel-<index>`; a tab without one gets no `aria-controls`. Default: all. */
  panels?: boolean[]
}

const SharedTabs = ({ tabs, value, onChange, ariaLabel = 'tabs', panels }: SharedTabsProps) => {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  return (
    <Box
      sx={{
        display: 'inline-flex',
        maxWidth: '100%',
        bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9',
        border: `2px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
        borderRadius: '10px',
        p: 0.5,
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Tabs
        value={value}
        onChange={(_, newValue) => onChange(newValue)}
        aria-label={ariaLabel}
        variant="scrollable"
        scrollButtons={false}
        TabIndicatorProps={{ sx: { display: 'none' } }}
        sx={{
          minHeight: 36,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: { xs: '12px', sm: '13px' },
            minHeight: 34,
            px: { xs: 1.5, sm: 2.5 },
            py: 0.5,
            whiteSpace: 'nowrap',
            borderRadius: '8px',
            color: isDark ? '#64748b' : '#94a3b8',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: isDark ? '#cbd5e1' : '#475569',
            },
            '&.Mui-selected': {
              color: isDark ? '#e2e8f0' : '#1e293b',
              bgcolor: isDark ? 'rgba(255,255,255,0.1)' : '#ffffff',
              boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.08)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0'}`,
            },
          },
        }}
      >
        {tabs.map((label, index) => (
          <Tab
            key={index}
            label={label}
            id={`shared-tab-${index}`}
            aria-controls={!panels || panels[index] ? `shared-tabpanel-${index}` : undefined}
          />
        ))}
      </Tabs>
    </Box>
  )
}

export default SharedTabs
